import React, { useState, useEffect } from 'react';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Helmet } from 'react-helmet-async';
import { Search, HelpCircle, MessageCircle, Phone, Mail, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getBackendUrl } from '@/config';

const API_URL = getBackendUrl();

export default function FAQPage() {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/faq/public`);
      if (response.ok) {
        const data = await response.json();
        setFaqs(data);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(data.map(faq => faq.category))];
        setCategories(['All', ...uniqueCategories]);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter FAQs based on search and category
  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Group filtered FAQs by category
  const faqsByCategory = filteredFaqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {});

  return (
    <PublicLayout>
      <Helmet>
        <title>Frequently Asked Questions | Kosha Tax Consultants</title>
        <meta name="description" content="Find answers to common questions about tax filing, LLC formation, payments, and more at Kosha Tax Consultants." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full mb-6">
              <HelpCircle className="h-5 w-5" />
              <span className="text-sm font-medium">Help Center</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Find quick answers to common questions about our services
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 text-lg bg-white text-foreground"
                data-testid="faq-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Category Filter */}
            {categories.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-8 justify-center">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                    data-testid={`category-${category.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredFaqs.length === 0 ? (
              /* No Results */
              <Card className="text-center py-12">
                <CardContent>
                  <HelpCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    {searchQuery ? 'No matching questions found' : 'No FAQs available'}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {searchQuery 
                      ? 'Try adjusting your search terms or browse by category'
                      : 'Check back later for frequently asked questions'
                    }
                  </p>
                  {searchQuery && (
                    <Button variant="outline" onClick={() => setSearchQuery('')}>
                      Clear Search
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              /* FAQ Accordion */
              <div className="space-y-8">
                {Object.entries(faqsByCategory).map(([category, categoryFaqs]) => (
                  <div key={category}>
                    <div className="flex items-center gap-2 mb-4">
                      <Badge variant="outline" className="text-sm">
                        {category}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        ({categoryFaqs.length} {categoryFaqs.length === 1 ? 'question' : 'questions'})
                      </span>
                    </div>
                    
                    <Card>
                      <CardContent className="p-0">
                        <Accordion type="single" collapsible className="w-full">
                          {categoryFaqs
                            .sort((a, b) => a.order - b.order)
                            .map((faq, index) => (
                              <AccordionItem 
                                key={faq.id} 
                                value={faq.id}
                                className={index === categoryFaqs.length - 1 ? 'border-b-0' : ''}
                              >
                                <AccordionTrigger className="px-6 text-left hover:no-underline">
                                  <span className="font-medium">{faq.question}</span>
                                </AccordionTrigger>
                                <AccordionContent className="px-6 pb-4">
                                  <p className="text-muted-foreground whitespace-pre-wrap">
                                    {faq.answer}
                                  </p>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                        </Accordion>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Still Need Help Section */}
      <section className="py-12 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white border-0">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold mb-2">Still Have Questions?</h2>
                  <p className="text-blue-100">
                    Our team is here to help you with any questions you may have
                  </p>
                </div>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      <MessageCircle className="h-6 w-6" />
                    </div>
                    <h3 className="font-semibold mb-1">Live Chat</h3>
                    <p className="text-sm text-blue-100 mb-3">Chat with our support team</p>
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => navigate('/contact')}
                    >
                      Start Chat
                    </Button>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Mail className="h-6 w-6" />
                    </div>
                    <h3 className="font-semibold mb-1">Email Support</h3>
                    <p className="text-sm text-blue-100 mb-3">help@koshatax.com</p>
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => window.location.href = 'mailto:help@koshatax.com'}
                    >
                      Send Email
                    </Button>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Phone className="h-6 w-6" />
                    </div>
                    <h3 className="font-semibold mb-1">Phone</h3>
                    <p className="text-sm text-blue-100 mb-3">Mon-Fri, 9am-6pm CST</p>
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => navigate('/contact')}
                    >
                      Contact Us
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
